"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeDrawState = exports.DEFAULT_SCALE = exports.IkeDrawTool = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const gamma_brightnessCorrection_1 = require("../model/collection/brightnessCorrection/gamma.brightnessCorrection");
const free_cropParameter_1 = require("../model/collection/cropParameter/free.cropParameter");
const hidden_imageVisibility_1 = require("../model/collection/imageVisibility/hidden.imageVisibility");
const unit_1 = require("../unit");
const ArrangeAnnotations_1 = require("./ArrangeAnnotations");
const image_zoom_binding_1 = require("./bindings/image.zoom.binding");
var IkeDrawTool;
(function (IkeDrawTool) {
    IkeDrawTool["MARKER"] = "marker";
    IkeDrawTool["LINE"] = "line";
    IkeDrawTool["LINK"] = "link";
    IkeDrawTool["BASE"] = "base";
})(IkeDrawTool || (exports.IkeDrawTool = IkeDrawTool = {}));
exports.DEFAULT_SCALE = 4;
class IkeDrawState {
    constructor(draw) {
        this.zoom = IkeDrawState.ZOOM_DEFAULT;
        this.cablemeasure = IkeDrawState.CABLEMEASURE_DEFAULT;
        this.showCablemeasure = false;
        this._brightness = 1;
        this._imageVisibilityHidden = false;
        this._cropX = 0;
        this._cropY = 0;
        this._cropWidth = 0;
        this._cropHeight = 0;
        /**
         * this scale is actually static, the zoom is used to determine how large things are drawn on the screen.
         * often times the svgRender ctx/options will have a 'scale' property, which is calculated using zoom level and NOT this scale.
         */
        this.scale = exports.DEFAULT_SCALE;
        this.centerX = 0;
        this.centerY = 0;
        this.selectedRegion = null;
        this.selectedId = null;
        this.brightnessCorrectionId = null;
        this.imageVisibilityId = null;
        this.defaultBrightness = 1;
        this.defaultImageVisibilityHidden = false;
        this.defaultCrop = { x: 0, y: 0, width: 0, height: 0 };
        this.cropParameterId = null;
        this.hoverId = null;
        this.moveId = null;
        this.linkFieldIds = [];
        /* Trigger the properties dialog */
        this.propertiesId = null;
        this.isCrosshairVisible = true;
        this.isDateLabelVisible = false;
        this.isBearingLabelVisible = false;
        this.isRollVisible = false;
        this.isInfoVisible = false;
        this.visibleInsightMasks = false;
        this.visibleInsightMarkers = false;
        this.autoArrangeAnnotations = false;
        this.mouseDownX = null;
        this.mouseDownY = null;
        this._tool = null;
        this.draw = draw;
        //if there is an already existing brightnessCorrection value, we apply it.
        const values = this.draw.brightnessCorrectionState.values();
        let id = '';
        //We go through all the brightness correction for this capture and keep the last id (there shall be only one brightness correction)
        for (let val of values) {
            id = val.id;
        }
        if (draw.brightnessCorrectionState.hasBrightnessCorrection(id)) {
            const gamma = draw.brightnessCorrectionState.getBrightnessCorrection(id);
            this.setBrightnessCorrectionId(id);
            this.setBrightness(gamma.exponent);
            this.defaultBrightness = gamma.exponent;
        }
        if (draw.imageVisibilityState.hasImageVisibility()) {
            const visibility = draw.imageVisibilityState.getImageVisibility();
            this.setImageVisibilityId(visibility.id);
            this.setImageVisibilityHidden(visibility.hidden);
            this.defaultImageVisibilityHidden = visibility.hidden;
        }
        const cropValues = this.draw.cropParameterState.values();
        let cropId = '';
        for (let val of cropValues) {
            cropId = val.id;
        }
        if (draw.cropParameterState.hasCropParameter(cropId)) {
            const free = draw.cropParameterState.getCropParameter(cropId);
            this.setCropParameterId(cropId);
            this.setCrop(free.x, free.y, free.width, free.height);
            if (free.width > 0 && free.height > 0) {
                this.setZoom(draw.capture.resolution.width / free.width); //zoom lvl
                this.setCenter(free.x + free.width * 0.5, free.y + free.height * 0.5);
            }
            this.defaultCrop = free;
        }
        if (this.draw.drawState.hasInsightDetections()) {
            this.visibleInsightMarkers = true;
        }
    }
    get tool() {
        // If the image is not classed as a markable image, then no tools are available.
        if (!this.draw.isMarkable) {
            return null;
        }
        // If the image contains any user created measurements, then use the selected tool
        // Note: A standard base measurement is considered a user measurement
        if (this.draw.drawState.hasUserMeasurements()) {
            return this._tool;
        }
        // Otherwise, only the base tool is available
        return 'base';
    }
    setTool(tool) {
        this._tool = tool;
        this.linkFieldIds = [];
    }
    toggleTool(tool) {
        if (this.tool === tool) {
            this._tool = null;
        }
        else {
            this._tool = tool;
        }
    }
    get brightness() {
        const brightness = this._brightness;
        if (brightness == null || isNaN(brightness)) {
            return IkeDrawState.BRIGHTNESS_DEFAULT;
        }
        return brightness;
    }
    get imageVisibilityHidden() {
        const imageHidden = this._imageVisibilityHidden;
        if (!imageHidden) {
            return IkeDrawState.IMAGE_VISIBILITY_HIDDEN_DEFAULT;
        }
        return imageHidden;
    }
    setBrightness(brightness) {
        if (brightness == null) {
            this._brightness = null;
            return;
        }
        //if there is no existing brightness correction state yet, we create one before setting its value.
        if (this.brightnessCorrectionId == null) {
            const newGammaId = this.draw.brightnessCorrectionState.Mutator.create('gamma');
            this.setBrightnessCorrectionId(newGammaId);
        }
        if (brightness > IkeDrawState.BRIGHTNESS_DEFAULT - IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW &&
            brightness < IkeDrawState.BRIGHTNESS_DEFAULT + IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW) {
            this._brightness = unit_1.IkeUnit.round(brightness, 2);
        }
        else {
            this._brightness = unit_1.IkeUnit.round(brightness, 5);
        }
        if (this.brightnessCorrectionId) {
            const gamma = this.draw.brightnessCorrectionState.getBrightnessCorrection(this.brightnessCorrectionId);
            if (gamma_brightnessCorrection_1.CollectionModelGammaBrightnessCorrection.isGamma(gamma)) {
                gamma.Mutator.setExponent(this._brightness);
            }
        }
    }
    setImageVisibilityHidden(hidden) {
        //if there is no existing image visibility state yet, we create one before setting its value.
        if (this.imageVisibilityId == null) {
            const newHiddenImageVisibilityId = this.draw.imageVisibilityState.Mutator.create('hidden');
            this.setImageVisibilityId(newHiddenImageVisibilityId);
        }
        const visibility = this.draw.imageVisibilityState.getImageVisibility();
        this._imageVisibilityHidden = hidden;
        if (hidden_imageVisibility_1.CollectionModelHiddenImageVisibility.isHidden(visibility)) {
            visibility.Mutator.setHidden(hidden);
        }
    }
    toggleImageVisibility() {
        this.setImageVisibilityHidden(!this._imageVisibilityHidden);
    }
    brightnessIncrease() {
        if (this.canIncreaseBrightness) {
            const newBrightness = this.brightness / IkeDrawState.BRIGHTNESS_DELTA;
            this.setBrightness(newBrightness);
        }
    }
    brightnessDecrease() {
        if (this.canDecreaseBrightness) {
            const newBrightness = this.brightness * IkeDrawState.BRIGHTNESS_DELTA;
            this.setBrightness(newBrightness);
        }
    }
    get canIncreaseBrightness() {
        const result = this.brightness > IkeDrawState.BRIGHTNESS_MIN;
        return result;
    }
    get canDecreaseBrightness() {
        const result = this.brightness < IkeDrawState.BRIGHTNESS_MAX;
        return result;
    }
    get canIncreaseCablemeasure() {
        return this.cablemeasure + IkeDrawState.CABLEMEASURE_STEP <= IkeDrawState.CABLEMEASURE_MAX;
    }
    get canDecreaseCablemeasure() {
        return this.cablemeasure - IkeDrawState.CABLEMEASURE_STEP >= IkeDrawState.CABLEMEASURE_MIN;
    }
    brightnessReset() {
        this.setBrightness(IkeDrawState.BRIGHTNESS_DEFAULT);
    }
    get markerOffsetWidth() {
        return 200 - this.zoom * 10;
    }
    get cropX() {
        const cropX = this._cropX;
        if (cropX == null || isNaN(cropX)) {
            return IkeDrawState.CROP_DEFAULT;
        }
        return cropX;
    }
    get cropY() {
        const cropY = this._cropY;
        if (cropY == null || isNaN(cropY)) {
            return IkeDrawState.CROP_DEFAULT;
        }
        return cropY;
    }
    get cropWidth() {
        const cropWidth = this._cropWidth;
        if (cropWidth == null || isNaN(cropWidth)) {
            return IkeDrawState.CROP_DEFAULT;
        }
        return cropWidth;
    }
    get cropHeight() {
        const cropHeight = this._cropHeight;
        if (cropHeight == null || isNaN(cropHeight)) {
            return IkeDrawState.CROP_DEFAULT;
        }
        return cropHeight;
    }
    setCrop(x, y, width, height) {
        if (x == null || y == null || width == null || height == null) {
            this._cropX = null;
            this._cropY = null;
            this._cropWidth = null;
            this._cropHeight = null;
            return;
        }
        //if there is no existing crop parameter state yet, we create one before setting its value.
        if (this.cropParameterId == null) {
            const newFreeId = this.draw.cropParameterState.Mutator.create('free');
            this.setCropParameterId(newFreeId);
        }
        this._cropX = x;
        this._cropY = y;
        this._cropWidth = width;
        this._cropHeight = height;
        if (this.cropParameterId) {
            const free = this.draw.cropParameterState.getCropParameter(this.cropParameterId);
            if (free_cropParameter_1.CollectionModelFreeCropParameter.isFree(free)) {
                free.Mutator.setCrop(this._cropX, this._cropY, this._cropWidth, this._cropHeight);
            }
        }
    }
    cropReset() {
        this.setCrop(IkeDrawState.CROP_DEFAULT, IkeDrawState.CROP_DEFAULT, IkeDrawState.CROP_DEFAULT, IkeDrawState.CROP_DEFAULT);
    }
    ///////////////////////////////////
    setCrosshair(show) {
        this.isCrosshairVisible = show;
    }
    setDatelabel(show) {
        this.isDateLabelVisible = show;
    }
    setBearingLabelVisible(show) {
        this.isBearingLabelVisible = show;
    }
    get center() {
        return {
            x: this.centerX,
            y: this.centerY,
        };
    }
    setHovered(hoverId) {
        this.hoverId = hoverId;
    }
    setCenter(x, y) {
        this.centerX = x;
        this.centerY = y;
    }
    setSelected(id, region) {
        this.selectedId = id;
        this.selectedRegion = region;
    }
    setBrightnessCorrectionId(id) {
        this.brightnessCorrectionId = id;
    }
    setImageVisibilityId(id) {
        this.imageVisibilityId = id;
    }
    setCropParameterId(id) {
        this.cropParameterId = id;
    }
    setZoom(zoom, x, y) {
        if (x != null && y != null) {
            this.setCenter(x, y);
        }
        this.zoom = unit_1.IkeUnit.round(zoom, 2);
    }
    zoomIncrease() {
        this.setZoom(Math.min(IkeDrawState.ZOOM_MAX, this.zoom * IkeDrawState.ZOOM_DELTA));
    }
    zoomDecrease() {
        this.setZoom(Math.max(IkeDrawState.ZOOM_DEFAULT, this.zoom * (1 / IkeDrawState.ZOOM_DELTA)));
    }
    zoomReset() {
        this.setZoom(IkeDrawState.ZOOM_DEFAULT);
    }
    toggleCablemeasure() {
        this.showCablemeasure = !this.showCablemeasure;
    }
    cablemeasureIncrease() {
        let actualMeasure = this.cablemeasure + IkeDrawState.CABLEMEASURE_STEP;
        if (actualMeasure > IkeDrawState.CABLEMEASURE_MAX) {
            actualMeasure = IkeDrawState.CABLEMEASURE_MAX;
        }
        this.cablemeasure = actualMeasure;
    }
    cablemeasureDecrease() {
        let actualMeasure = this.cablemeasure - IkeDrawState.CABLEMEASURE_STEP;
        if (actualMeasure < IkeDrawState.CABLEMEASURE_MIN) {
            actualMeasure = IkeDrawState.CABLEMEASURE_MIN;
        }
        this.cablemeasure = actualMeasure;
    }
    propertiesShow(drawableId) {
        this.propertiesId = drawableId;
    }
    propertiesHide() {
        this.propertiesId = null;
    }
    setLinkFields(fieldIds) {
        if (fieldIds == null || fieldIds.length === 0) {
            this.linkFieldIds = [];
            if (this.tool !== IkeDrawTool.BASE) {
                this._tool = null;
            }
        }
        else {
            this.linkFieldIds = fieldIds;
            this._tool = IkeDrawTool.MARKER;
        }
    }
    setFromState(other) {
        if (other == null) {
            return;
        }
        if (this.brightness !== other.brightness) {
            this.setBrightness(other.brightness);
        }
        if (this.cropX !== other.cropX ||
            this.cropY !== other.cropY ||
            this.cropHeight !== other.cropHeight ||
            this.cropWidth !== other.cropWidth) {
            this.setCrop(other.cropX, other.cropY, other.cropWidth, other.cropHeight);
        }
        this.setZoom(other.zoom, other.centerX, other.centerY);
    }
    setInfoVisible(visible) {
        this.isInfoVisible = visible;
        this.isBearingLabelVisible = visible;
        this.isDateLabelVisible = visible;
        this.isRollVisible = visible;
    }
    setInsightMasksVisible(visible) {
        this.visibleInsightMasks = visible;
    }
    setInsightMarkersVisible(visible) {
        this.visibleInsightMarkers = visible;
    }
    setAutoArrangeAnnotations(autoArrange, renderOptions) {
        this.autoArrangeAnnotations = autoArrange;
        if (this.autoArrangeAnnotations && renderOptions) {
            this.draw.state.arrangeAnnotations(renderOptions);
        }
    }
    arrangeAnnotations(renderOptions) {
        new ArrangeAnnotations_1.ArrangeAnnotations(this.draw, renderOptions).run();
    }
}
exports.IkeDrawState = IkeDrawState;
IkeDrawState.BRIGHTNESS_DELTA = 1.2;
IkeDrawState.BRIGHTNESS_MIN = 0.2;
IkeDrawState.BRIGHTNESS_MAX = 5;
IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW = 0.1;
IkeDrawState.BRIGHTNESS_DEFAULT = 1;
IkeDrawState.IMAGE_VISIBILITY_HIDDEN_DEFAULT = false;
IkeDrawState.ZOOM_DELTA = 1.1;
IkeDrawState.ZOOM_MAX = image_zoom_binding_1.MAX_ZOOM_LEVEL;
IkeDrawState.ZOOM_DEFAULT = image_zoom_binding_1.START_ZOOM_LEVEL;
IkeDrawState.CABLEMEASURE_DEFAULT = 1; // default cablemesure value in inch
IkeDrawState.CABLEMEASURE_STEP = 0.5;
IkeDrawState.CABLEMEASURE_MIN = 0.5;
IkeDrawState.CABLEMEASURE_MAX = 4;
IkeDrawState.CROP_DEFAULT = 0; //default setting for x,y,width, and height.
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "zoom", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "cablemeasure", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "showCablemeasure", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_brightness", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_imageVisibilityHidden", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_cropX", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_cropY", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_cropWidth", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_cropHeight", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "scale", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "centerX", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "centerY", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "selectedRegion", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "selectedId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "brightnessCorrectionId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "imageVisibilityId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "defaultBrightness", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "defaultImageVisibilityHidden", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "defaultCrop", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "cropParameterId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "hoverId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "moveId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "linkFieldIds", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "propertiesId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "isCrosshairVisible", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "isDateLabelVisible", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "isBearingLabelVisible", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "isRollVisible", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "isInfoVisible", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "visibleInsightMasks", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "visibleInsightMarkers", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "autoArrangeAnnotations", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "mouseDownX", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "mouseDownY", void 0);
tslib_1.__decorate([
    mobx_1.observable
], IkeDrawState.prototype, "_tool", void 0);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "tool", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setTool", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "toggleTool", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "brightness", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "imageVisibilityHidden", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setBrightness", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setImageVisibilityHidden", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "toggleImageVisibility", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "brightnessIncrease", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "brightnessDecrease", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "canIncreaseBrightness", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "canDecreaseBrightness", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "canIncreaseCablemeasure", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "canDecreaseCablemeasure", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "brightnessReset", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "markerOffsetWidth", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "cropX", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "cropY", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "cropWidth", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "cropHeight", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setCrop", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "cropReset", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setCrosshair", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setDatelabel", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setBearingLabelVisible", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDrawState.prototype, "center", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setHovered", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setCenter", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setSelected", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setBrightnessCorrectionId", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setImageVisibilityId", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setCropParameterId", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setZoom", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "zoomIncrease", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "zoomDecrease", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "zoomReset", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "toggleCablemeasure", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "cablemeasureIncrease", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "cablemeasureDecrease", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "propertiesShow", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "propertiesHide", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setLinkFields", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setInsightMasksVisible", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "setInsightMarkersVisible", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDrawState.prototype, "arrangeAnnotations", null);
//# sourceMappingURL=draw.state.js.map